import type { Banner } from "@/lib/marketing/banners-api";

/** Les champs dont l'aperçu a besoin — un brouillon en cours de saisie suffit. */
export type BannerApercu = Pick<
	Banner,
	"template" | "title" | "eyebrow" | "ctaLabel" | "color"
> &
	Partial<
		Pick<
			Banner,
			"imageUrl" | "imageAlt" | "discount" | "code" | "thumbs" | "categorySlugs"
		>
	>;

/**
 * Aperçu d'une bannière, à l'échelle de la carte.
 *
 * Chaque modèle a son propre rendu : c'est ce qui permet de choisir un modèle
 * en le voyant plutôt qu'en lisant son nom. L'aperçu est volontairement une
 * approximation — il montre la structure et les couleurs, pas la typographie
 * exacte de la vitrine, que seule la vitrine sait produire.
 */
export function BannerPreview({ b }: { b: BannerApercu }) {
	if (b.imageUrl) {
		return (
			<div className="banner-preview" style={{ background: b.color }}>
				<img className="bp-img" src={b.imageUrl} alt={b.imageAlt ?? ""} />
			</div>
		);
	}

	return (
		<div className="banner-preview" style={{ background: b.color }}>
			<span className="bp-deco" />
			{b.eyebrow && <div className="bp-eyebrow">{b.eyebrow}</div>}
			<div className="bp-title">{b.title || "Titre de la bannière"}</div>

			{b.template === "promo" && (
				<div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
					{b.discount && (
						<span
							style={{
								fontFamily: "var(--a-font-display)",
								fontWeight: 900,
								fontSize: 22,
							}}
						>
							{b.discount}
						</span>
					)}
					{b.code && (
						<span
							style={{
								fontFamily: "var(--a-font-mono)",
								fontSize: 10,
								letterSpacing: "0.1em",
								background: "rgba(255,255,255,0.22)",
								padding: "3px 8px",
								borderRadius: 999,
							}}
						>
							{b.code}
						</span>
					)}
				</div>
			)}

			{b.template === "collection" && (b.thumbs ?? []).length > 0 && (
				<div style={{ display: "flex", gap: 5, marginTop: 8 }}>
					{(b.thumbs ?? []).slice(0, 3).map((t, i) => (
						<span
							key={`${t}-${i}`}
							style={{
								width: 26,
								height: 26,
								borderRadius: 5,
								background: t,
								border: "1px solid rgba(255,255,255,0.4)",
							}}
						/>
					))}
				</div>
			)}

			{b.template === "category" && (b.categorySlugs ?? []).length > 0 && (
				<div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
					{(b.categorySlugs ?? []).slice(0, 4).map((c) => (
						<span
							key={c}
							style={{
								fontSize: 9,
								background: "rgba(255,255,255,0.22)",
								padding: "2px 7px",
								borderRadius: 999,
							}}
						>
							{c}
						</span>
					))}
				</div>
			)}

			{b.ctaLabel && <div className="bp-cta">{b.ctaLabel}</div>}
		</div>
	);
}
